import { CreationSourceEnum } from 'js/src/client';
import { FRESH_ANIMATION_DATA } from 'js/src/components/animated-explainer-videos/constants';
import { migrateAnimationData } from 'js/src/components/animated-explainer-videos/migrations';
import type { AnimationData } from 'js/src/components/animated-explainer-videos/migrations/types/current';
import type { StylePreset } from 'js/src/components/animated-explainer-videos/utils/stylePresets';

/**
 * The toolbox keeps an animated explainer's style preset and ingredients in the video's
 * `animation_data`, in the GenX editor's own shape and under its migration chain, so the two
 * share one format. Null until a preset or ingredient exists. Only the creation source tells
 * the editor's videos from the toolbox's; the data itself does not.
 */
export type AnimatedExplainerState = AnimationData | null;

export function isGenxCreationSource(source: CreationSourceEnum | null | undefined): boolean {
    return source === CreationSourceEnum.GENX || source === CreationSourceEnum.GENX_REPEAT;
}

/** The toolbox's starting record: the editor's defaults, but never routed into the editor. */
export function freshToolboxAnimationData(): AnimationData {
    return { ...FRESH_ANIMATION_DATA, isAnimationEditorActive: false };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A saved video's animation data brought to the current shape, or null when it has none. */
export function readAnimatedExplainerState(animationData: unknown): AnimatedExplainerState {
    if (!isRecord(animationData) || typeof animationData.version !== 'number') {
        return null;
    }
    return migrateAnimationData(animationData).data;
}

/** The preset's style snapshotted onto the data, as the editor's `selectStylePreset` does. */
export function withStylePreset(data: AnimationData, preset: StylePreset): AnimationData {
    return {
        ...data,
        selectedPresetId: preset.id,
        selectedTemplateId: preset.templateId,
        styleReferenceAssets: preset.styleReferenceAssets,
        imageStyleGuidelines: preset.imageStyleGuidelines,
        videoStyleGuidelines: preset.videoStyleGuidelines,
        videoNegativePrompt: preset.videoNegativePrompt,
    };
}

export function normalizeIngredientName(name: string): string {
    return name
        .trim()
        .replace(/^@/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
